import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BookOpenCheck, CalendarClock, ExternalLink, Flame, GraduationCap, Lightbulb, LockOpen, MessageCircle, MousePointer2, Search, Sparkles, TimerReset, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/services/api";
import ProgressRing from "@/components/ProgressRing";
import ScenarioCard from "@/components/ScenarioCard";
import StatTile from "@/components/StatTile";

const HERO_INITIAL = { opacity: 0, y: 18 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const DISCORD_URL = "https://discord.gg/F6pQWdZnWA";

function LiveClassHomeCard() {
  return (
    <section className="mt-8 live-home-card" data-testid="dashboard-live-class-card">
      <div>
        <p className="section-kicker" data-testid="dashboard-live-class-kicker">conversação ao vivo</p>
        <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl" data-testid="dashboard-live-class-title">Aula no Discord todo domingo.</h2>
        <p className="mt-3 max-w-3xl text-sm font-bold text-white/75 md:text-base" data-testid="dashboard-live-class-description">Entre para praticar conversação comigo por 1 hora, todo domingo às 13:00 no horário de Brasília.</p>
      </div>
      <div className="live-home-actions" data-testid="dashboard-live-class-actions">
        <span className="live-home-time" data-testid="dashboard-live-class-time"><CalendarClock size={20} /> Domingo · 13:00 BRT</span>
        <a className="primary-action w-auto" href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="dashboard-live-class-discord-link">
          <MessageCircle size={18} /> Entrar no Discord <ExternalLink size={16} />
        </a>
      </div>
    </section>
  );
}

function UnlockDashboardCta({ navigate }) {
  return (
    <section className="modo-unlock-dashboard-cta" data-testid="modo-destravar-dashboard-cta">
      <div>
        <p className="section-kicker" data-testid="dashboard-unlock-kicker">modo destravar</p>
        <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl" data-testid="dashboard-unlock-title">Treine resposta rápida antes da mente travar.</h2>
        <p className="mt-3 max-w-3xl text-sm font-bold text-white/75 md:text-base" data-testid="dashboard-unlock-description">Pressão controlada, frase segura e feedback de clareza, velocidade e confiança.</p>
      </div>
      <button type="button" className="primary-action w-auto" onClick={() => navigate("/unlock")} data-testid="dashboard-unlock-start-button">
        <LockOpen size={18} /> Iniciar Modo Destravar <Zap size={16} />
      </button>
    </section>
  );
}

function StartHereSection({ navigate }) {
  const steps = [
    { number: 1, title: "Clique em Falar", text: "Treine uma frase curta com ajuda e correção.", action: "Começar exercício de fala", route: "/unlock", icon: LockOpen, testId: "btn-start-step-1" },
    { number: 2, title: "Escolha Aulas", text: "Veja exercícios organizados em aulas simples.", action: "Encontrar aulas", route: "/modules", icon: BookOpenCheck, testId: "btn-start-step-2" },
    { number: 3, title: "Ao Vivo no domingo", text: "Entre na aula de conversação às 13:00 BRT.", action: "Ver aula ao vivo", route: "/live", icon: CalendarClock, testId: "btn-start-step-3" },
  ];
  return (
    <section className="start-here-panel" data-testid="start-here-section">
      <div className="text-center">
        <p className="section-kicker" data-testid="start-here-kicker">comece aqui</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl" data-testid="start-here-title">Encontre seu exercício em 3 passos.</h1>
        <p className="mx-auto mt-4 max-w-3xl text-lg font-bold text-white/75" data-testid="start-here-description">Para não se perder: comece falando, depois faça uma aula e volte para praticar ao vivo.</p>
      </div>
      <div className="start-here-grid" data-testid="start-here-steps-grid">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <article className="start-step-card" key={step.number} data-testid={`start-step-card-${step.number}`}>
              <span className="start-step-number" data-testid={`start-step-number-${step.number}`}>{step.number}</span>
              <Icon size={32} />
              <h2 data-testid={`start-step-title-${step.number}`}>{step.title}</h2>
              <p data-testid={`start-step-text-${step.number}`}>{step.text}</p>
              <button type="button" className="primary-action w-full" onClick={() => navigate(step.route)} data-testid={step.testId}>{step.action}</button>
            </article>
          );
        })}
      </div>
      <button type="button" className="exercise-finder-button" onClick={() => navigate("/modules")} data-testid="dashboard-find-exercises-button">
        <Search size={26} /> Encontrar Exercícios <MousePointer2 size={22} />
      </button>
    </section>
  );
}

function StatsSection({ progress }) {
  return (
    <section className="mt-8 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]" data-testid="daily-progress-section">
      <div className="grid gap-5 sm:grid-cols-3">
        <StatTile icon={Flame} label="Sequência" value={`${progress?.streak_days || 0} dias`} tone="surface-yellow" testId="streak-stat-tile" />
        <StatTile icon={GraduationCap} label="Sessões" value={progress?.sessions_completed || 0} tone="surface-mint" testId="sessions-stat-tile" />
        <StatTile icon={TimerReset} label="Minutos" value={progress?.minutes_practiced || 0} tone="surface-blue" testId="minutes-stat-tile" />
      </div>
      <article className="neo-card surface-neutral flex items-center gap-5 p-5" data-testid="goal-progress-card">
        <ProgressRing value={progress?.daily_goal_progress || 0} label="meta" testId="daily-goal-progress-ring" />
        <div>
          <p className="text-xs font-black uppercase text-slate-500" data-testid="goal-progress-label">Meta diária</p>
          <h2 className="mt-2 font-display text-3xl font-black" data-testid="goal-progress-title">15 minutos de fala real</h2>
          <p className="mt-2 text-sm font-semibold text-slate-600" data-testid="goal-progress-detail">
            Última prática: {progress?.last_session_title || "comece sua primeira simulação"}
          </p>
        </div>
      </article>
    </section>
  );
}

function DashboardHero({ featured, navigate, startScenario }) {
  return (
    <section className="hero-band" data-testid="daily-lesson-hero">
      <div className="hero-overlay" />
      <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} className="relative z-10 max-w-3xl py-14 sm:py-20">
        <span className="hero-kicker" data-testid="daily-plan-kicker">aula de hoje · 15 minutos</span>
        <h1 className="mt-4 font-display text-4xl font-black leading-none sm:text-5xl lg:text-6xl" data-testid="dashboard-main-heading">Fale antes da mente travar.</h1>
        <p className="mt-5 max-w-2xl text-base font-bold text-slate-700 md:text-lg" data-testid="dashboard-hero-copy">
          Simulações curtas de situações reais nos EUA, com fala pelo microfone, resposta guiada e feedback para ganhar velocidade.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button type="button" className="primary-action w-auto" onClick={() => featured[0] && startScenario(featured[0].id)} data-testid="start-daily-lesson-button">
            Quero Falar Inglês <Sparkles size={19} strokeWidth={2.7} />
          </button>
          <button type="button" className="secondary-action" onClick={() => navigate("/scenarios")} data-testid="browse-scenarios-button">Ver biblioteca</button>
        </div>
      </motion.div>
    </section>
  );
}

function FeaturedScenarios({ featured, loadingScenarios, navigate, startScenario }) {
  return (
    <section className="mt-10" data-testid="featured-scenarios-section">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-kicker" data-testid="featured-section-kicker">comece por aqui</p>
          <h2 className="font-display text-3xl font-black" data-testid="featured-section-title">Cenários de alta frequência</h2>
        </div>
        <button type="button" className="secondary-action" onClick={() => navigate("/scenarios")} data-testid="see-all-scenarios-button">Todos os cenários</button>
      </div>
      {loadingScenarios ? (
        <div className="neo-card surface-neutral p-6" data-testid="scenarios-loading-state">Carregando cenários...</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="featured-scenarios-grid">
          {featured.map((scenario, index) => <ScenarioCard key={scenario.id} scenario={scenario} onStart={startScenario} index={index} />)}
        </div>
      )}
    </section>
  );
}

function grammarTotals(grammarModules = []) {
  const totalLessons = grammarModules.reduce((sum, item) => sum + item.progress.total_lessons, 0);
  const completedLessons = grammarModules.reduce((sum, item) => sum + item.progress.completed_lessons, 0);
  const percent = totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const nextModule = grammarModules.find((item) => item.progress.percent_complete < 100) || grammarModules[0];
  const nextLesson = nextModule?.module.lessons.find((lesson) => {
    const progress = nextModule.progress.lessons.find((entry) => entry.lesson_id === lesson.id);
    return progress?.status !== "completed";
  }) || nextModule?.module.lessons[0];
  return { totalLessons, completedLessons, percent, nextModule, nextLesson };
}

function GrammarProgressSection({ grammarModules, navigate }) {
  const totals = grammarTotals(grammarModules);
  return (
    <section className="mt-10" data-testid="dashboard-grammar-section">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-kicker" data-testid="dashboard-grammar-kicker">gramática guiada</p>
          <h2 className="font-display text-3xl font-black" data-testid="dashboard-grammar-title">Base para falar sem travar</h2>
        </div>
        <button type="button" className="secondary-action" onClick={() => navigate("/grammar")} data-testid="dashboard-open-grammar-button">
          Abrir trilha <BookOpenCheck size={18} />
        </button>
      </div>
      <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr]" data-testid="dashboard-grammar-grid">
        <article className="neo-card surface-mint flex items-center gap-5 p-5" data-testid="dashboard-grammar-overview-card">
          <ProgressRing value={totals.percent} label="gramática" testId="dashboard-grammar-progress-ring" />
          <div>
            <p className="text-xs font-black uppercase text-slate-500" data-testid="dashboard-grammar-progress-label">Progresso total</p>
            <h3 className="mt-2 font-display text-3xl font-black" data-testid="dashboard-grammar-progress-title">
              {totals.completedLessons}/{totals.totalLessons} aulas
            </h3>
            <p className="mt-2 text-sm font-semibold text-slate-600" data-testid="dashboard-grammar-next-lesson">
              Próxima: {totals.nextLesson?.title || "comece a trilha"}
            </p>
          </div>
        </article>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="dashboard-grammar-module-grid">
          {grammarModules.map((item) => (
            <article className="neo-card surface-neutral p-4" key={item.module.id} data-testid={`dashboard-grammar-module-${item.module.id}`}>
              <p className="text-xs font-black uppercase text-slate-500" data-testid={`dashboard-grammar-module-level-${item.module.id}`}>{item.module.level}</p>
              <h3 className="mt-1 font-display text-xl font-black" data-testid={`dashboard-grammar-module-title-${item.module.id}`}>{item.module.title}</h3>
              <div className="grammar-progress-track mt-3" data-testid={`dashboard-grammar-module-track-${item.module.id}`}>
                <span style={{ width: `${item.progress.percent_complete}%` }} data-testid={`dashboard-grammar-module-fill-${item.module.id}`} />
              </div>
              <p className="mt-2 text-sm font-black text-slate-600" data-testid={`dashboard-grammar-module-progress-${item.module.id}`}>
                {item.progress.completed_lessons}/{item.progress.total_lessons} aulas · {item.progress.percent_complete}%
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RecommendationsSection({ recommendations, navigate }) {
  return (
    <section className="mt-10" data-testid="dashboard-recommendations-section">
      <div className="mb-5 flex items-center gap-3">
        <Lightbulb size={28} />
        <div>
          <p className="section-kicker" data-testid="dashboard-recommendations-kicker">recomendado para você</p>
          <h2 className="font-display text-3xl font-black" data-testid="dashboard-recommendations-title">Próximos passos adaptativos</h2>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-recommendations-grid">
        {recommendations.map((item) => (
          <article className="neo-card surface-neutral p-4" key={item.id} data-testid={`recommendation-card-${item.id}`}>
            <span className="label-chip bg-white" data-testid={`recommendation-priority-${item.id}`}>{item.priority}</span>
            <h3 className="mt-3 font-display text-xl font-black" data-testid={`recommendation-title-${item.id}`}>{item.title}</h3>
            <p className="mt-2 text-sm font-bold text-slate-600" data-testid={`recommendation-reason-${item.id}`}>{item.reason_pt}</p>
            <button type="button" className="secondary-action mt-4 w-full" onClick={() => navigate(item.route)} data-testid={`recommendation-action-${item.id}`}>{item.action_label}</button>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: scenarios = [], isLoading: loadingScenarios } = useQuery({ queryKey: ["scenarios"], queryFn: api.getScenarios });
  const { data: progress } = useQuery({ queryKey: ["progress"], queryFn: api.getProgress });
  const { data: grammarModules = [] } = useQuery({ queryKey: ["grammar-modules"], queryFn: api.getGrammarModules });
  const { data: recommendations = [] } = useQuery({ queryKey: ["recommendations"], queryFn: api.getRecommendations });

  const startScenario = async (scenarioId) => {
    const session = await api.createSession(scenarioId);
    navigate(`/lesson/${scenarioId}?session=${session.id}`);
  };

  const featured = scenarios.slice(0, 3);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <StartHereSection navigate={navigate} />
      <DashboardHero featured={featured} navigate={navigate} startScenario={startScenario} />
      <UnlockDashboardCta navigate={navigate} />
      <LiveClassHomeCard />
      <StatsSection progress={progress} />
      <RecommendationsSection recommendations={recommendations} navigate={navigate} />
      <GrammarProgressSection grammarModules={grammarModules} navigate={navigate} />
      <FeaturedScenarios featured={featured} loadingScenarios={loadingScenarios} navigate={navigate} startScenario={startScenario} />
    </div>
  );
}